import { useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@workpay/ds-shared/components/ui/Button';
import { useTranslation } from '@hooks/useTranslation';
import { EorAuthLayout } from './EorAuthLayout';
import { StepTitle, StepSub, BackLink } from './components/StepBits';
import { isValidEmail } from './utils/email';
import { WORKSPACE_DOMAIN_SUFFIX } from '@lib/runtimeConfig';
import { useLocalizationStore } from '@stores/localizationStore';
import { workspaceDiscoveryService } from '@services/workspaceDiscoveryService';
import ArrowDownIcon from '@assets/icons/ArrowDownIcon';
import GlobeLockIcon from '@assets/icons/GlobeLockIcon';
import form from './EorForm.module.scss';
import styles from './FindWorkspace.module.scss';

export function FindWorkspace() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const language = useLocalizationStore((s) => s.language);

  const send = useMutation({
    mutationFn: (value: string) => workspaceDiscoveryService.issueChallenge(value, language),
    onSuccess: (res, value) =>
      navigate({
        to: '/eor/check-inbox',
        // BE always responds "sent" (anti-enumeration); it only emails when the
        // address maps to a workspace. challengeId drives resend; cd seeds the
        // resend cooldown.
        search: { email: res.email || value, challengeId: res.challengeId, cd: res.resendAvailableIn },
      }),
    onError: () =>
      setError(t('K_DS_EXT_EOR_FIND_GENERIC_ERROR', 'Something went wrong. Please try again.')),
  });

  function onSubmit(ev: FormEvent) {
    ev.preventDefault();
    const value = email.trim();
    if (!isValidEmail(value)) {
      setError(t('K_DS_EXT_EOR_FIND_INVALID', 'Please enter a valid work email address.'));
      return;
    }
    setError('');
    send.mutate(value);
  }

  return (
    <EorAuthLayout>
      <BackLink label={t('K_DS_EXT_EOR_BACK', 'Back')} onClick={() => navigate({ to: '/eor/workspace' })} />
      <StepTitle>{t('K_DS_EXT_EOR_FIND_TITLE', 'Find your EOR workspace')}</StepTitle>
      <StepSub>
        {t(
          'K_DS_EXT_EOR_FIND_SUB',
          "Your workspace lives at its own web address. Here's the part that's unique to your company:",
        )}
      </StepSub>

      <div className={styles.visualiserCard}>
        <div className={styles.urlRow}>
          <GlobeLockIcon className={styles.urlIcon} />
          <span>https://</span>
          <span className={styles.slug}>acme</span>
          <span>{WORKSPACE_DOMAIN_SUFFIX}</span>
        </div>
        <div className={styles.annotation}>
          <ArrowDownIcon className={styles.annotationArrow} />
          <span className={styles.annotationText}>
            {t('K_DS_EXT_EOR_FIND_URL_HINT', 'This part is your workspace name')}
          </span>
        </div>
      </div>

      <p className={form.helper}>
        {t(
          'K_DS_EXT_EOR_FIND_HELPER',
          "Not sure what yours is? Enter your work email and we'll look it up for you.",
        )}
      </p>

      <form onSubmit={onSubmit} noValidate>
        <label htmlFor="find-email" className={form.label}>
          {t('K_DS_EXT_EOR_FIND_EMAIL_LABEL', 'Work email address')}
        </label>
        <input
          id="find-email"
          type="email"
          className={`${form.input} ${error ? form.inputError : ''}`}
          placeholder="name@company.com"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setError('');
          }}
        />
        {error ? (
          <p role="alert" className={form.error}>
            {error}
          </p>
        ) : null}

        <Button type="submit" variant="primary" size="lg" loading={send.isPending} className={form.cta}>
          {t('K_DS_EXT_EOR_FIND_CTA', 'Find my workspace')}
        </Button>
      </form>
    </EorAuthLayout>
  );
}
